// src/pages/Tasks.jsx - Fully responsive with task types and detail drawer
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronDown,
  X,
  Calendar,
  User,
  MoreHorizontal,
  Check,
  Loader2,
  Phone,
  Mail,
  MessageCircle,
  ClipboardList,
  FileText,
} from "lucide-react";
import TaskDetailDrawer from "../components/TaskDetailDrawer";

// ---------- API helpers ----------
const BASE = process.env.REACT_APP_API_URL || "";
async function j(method, url, body) {
  const res = await fetch(`${BASE}${url}`, {
    method,
    credentials: 'include',
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${method} ${url} -> ${res.status} ${await res.text()}`);
  return res.json();
}
const listAll = (params = {}) => j("GET", `/tasks/all${qs(params)}`);
const listMine = (params = {}) => j("GET", `/tasks${qs(params)}`);
const summary = () => j("GET", "/tasks/summary");
const patchMine = (id, p) => j("PATCH", `/tasks/${id}`, p);
const delMine = (id) => j("DELETE", `/tasks/${id}`);
const createMine = (p) => j("POST", "/tasks", p);
const createClient = (cid, p) => j("POST", `/tasks/clients/${cid}`, p);
const patchClient = (cid, tid, p) => j("PATCH", `/tasks/clients/${cid}/${tid}`, p);
const getClients = () => j("GET", "/clients");
function qs(o) {
  const q = new URLSearchParams(Object.fromEntries(Object.entries(o).filter(([,v]) => v!=null && v!==""))).toString();
  return q ? `?${q}` : "";
}

// ---------- Task Type Config ----------
const TASK_TYPES = {
  call: { icon: Phone, label: 'Phone Call', color: 'bg-green-100 text-green-600', borderColor: 'border-green-300' },
  email: { icon: Mail, label: 'Send Email', color: 'bg-purple-100 text-purple-600', borderColor: 'border-purple-300' },
  sms: { icon: MessageCircle, label: 'Send SMS', color: 'bg-blue-100 text-blue-600', borderColor: 'border-blue-300' },
  follow_up: { icon: ClipboardList, label: 'Follow Up', color: 'bg-orange-100 text-orange-600', borderColor: 'border-orange-300' },
  meeting: { icon: Calendar, label: 'Meeting', color: 'bg-pink-100 text-pink-600', borderColor: 'border-pink-300' },
  document: { icon: FileText, label: 'Document', color: 'bg-yellow-100 text-yellow-600', borderColor: 'border-yellow-300' },
  general: { icon: ClipboardList, label: 'General', color: 'bg-slate-100 text-slate-600', borderColor: 'border-slate-300' }
};

// ---------- Date helpers ----------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const sameDay = (a, b) => startOfDay(a).getTime() === startOfDay(b).getTime();

// ---------- Mobile Task Card ----------
const TaskCard = React.forwardRef(({ task, selected, onSelect, onToggle, onClick, t, highlighted }, ref) => {
  const due = task.dueDate ? new Date(task.dueDate) : null;
  const isOverdue = due && due < new Date() && task.status !== "done" && task.status !== "completed";
  const isToday = due && sameDay(due, new Date());
  const isCompleted = task.status === "done" || task.status === "completed";

  const typeConfig = TASK_TYPES[task.taskType] || TASK_TYPES.general;
  const TypeIcon = typeConfig.icon;

  const priorityColors = {
    urgent: "bg-red-100 text-red-700 border-red-200",
    high: "bg-orange-100 text-orange-700 border-orange-200",
    normal: "bg-blue-100 text-blue-700 border-blue-200",
    low: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <div
      ref={ref}
      onClick={(e) => {
        if (e.target.type === 'checkbox') return;
        onClick(task);
      }}
      className={`rounded-xl shadow-sm border overflow-hidden transition-all cursor-pointer hover:shadow-md ${
        highlighted ? 'bg-yellow-50 ring-2 ring-yellow-400' :
        isCompleted ? "bg-white border-gray-200 opacity-60" :
        isOverdue ? "bg-white border-red-300" : "bg-white border-slate-200"
      }`}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(task);
            }}
            className="mt-1 w-5 h-5 rounded border-slate-300"
          />

          {/* Task Type Icon */}
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${typeConfig.color}`}>
            <TypeIcon size={20} />
          </div>

          {/* Task Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className={`font-semibold text-slate-900 ${isCompleted ? "line-through text-slate-500" : ""}`}>
                {task.title}
              </h3>
              {isCompleted && (
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                  <Check size={14} className="text-white" />
                </span>
              )}
            </div>

            {/* Meta Row */}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-0.5 text-xs rounded-full border ${priorityColors[task.priority] || priorityColors.normal}`}>
                {t(task.priority || "normal")}
              </span>

              <span className={`px-2 py-0.5 text-xs rounded-full ${typeConfig.color}`}>
                {typeConfig.label}
              </span>

              {task.clientName && (
                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <User size={12} />
                  {task.clientName}
                </span>
              )}
            </div>

            {/* Due Date */}
            {due && (
              <div className={`flex items-center gap-1 text-sm mt-2 ${
                isOverdue ? "text-red-600 font-medium" : isToday ? "text-orange-600" : "text-slate-500"
              }`}>
                <Clock size={14} />
                {isOverdue ? t('overdue') + ': ' : isToday ? t('today') + ': ' : ''}
                {due.toLocaleString()}
              </div>
            )}

            {task.description && (
              <p className="text-sm text-slate-500 mt-2 line-clamp-2">{task.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      {!isCompleted && (
        <div className="flex border-t border-slate-100 divide-x divide-slate-100">
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(task); }}
            className="flex-1 py-3 text-sm text-emerald-600 font-medium hover:bg-emerald-50 transition flex items-center justify-center gap-1"
          >
            <CheckCircle2 size={16} />
            {t('complete')}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClick(task); }}
            className="flex-1 py-3 text-sm text-slate-600 font-medium hover:bg-slate-50 transition"
          >
            {t('viewDetails')}
          </button>
        </div>
      )}
    </div>
  );
});

// ---------- Desktop Row ----------
const Row = React.forwardRef(({ task, selected, onSelect, onToggle, onClick, t, highlighted }, ref) => {
  const due = task.dueDate ? new Date(task.dueDate) : null;
  const isCompleted = task.status === "done" || task.status === "completed";
  const isOverdue = due && due < new Date() && !isCompleted;
  const typeConfig = TASK_TYPES[task.taskType] || TASK_TYPES.general;
  const TypeIcon = typeConfig.icon;

  return (
    <div
      ref={ref}
      onClick={(e) => {
        if (e.target.type === 'checkbox') return;
        onClick(task);
      }}
      className={`grid grid-cols-12 items-center gap-3 px-4 py-4 border-b border-slate-100 last:border-0 transition-all cursor-pointer hover:bg-slate-50 ${
        highlighted ? 'bg-yellow-50 ring-2 ring-yellow-400' : 'bg-white'
      }`}
    >
      <div className="col-span-1 flex items-center gap-2">
        <input
          type="checkbox"
          className="w-4 h-4 rounded"
          checked={selected}
          onChange={(e) => { e.stopPropagation(); onSelect(task); }}
        />
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeConfig.color}`}>
          <TypeIcon size={16} />
        </div>
      </div>

      <div className="col-span-5">
        <div className={`font-medium ${isCompleted ? "line-through text-gray-500" : ""}`}>
          {task.title}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`px-2 py-0.5 text-xs rounded-full ${typeConfig.color}`}>
            {typeConfig.label}
          </span>
          {task.clientName && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <User size={12} />
              {task.clientName}
            </span>
          )}
        </div>
      </div>

      <div className="col-span-2">
        <span className={`px-2 py-1 text-xs rounded-full border ${
          task.priority === "urgent" ? "bg-red-50 border-red-200 text-red-700" :
          task.priority === "high" ? "bg-orange-50 border-orange-200 text-orange-700" :
          task.priority === "low" ? "bg-gray-50 border-gray-200" : "bg-blue-50 border-blue-200 text-blue-700"
        }`}>
          {t(task.priority || "normal")}
        </span>
      </div>

      <div className="col-span-2">
        {due ? (
          <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-600'}`}>
            {due.toLocaleString()}
          </span>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        )}
      </div>

      <div className="col-span-2 flex items-center justify-end gap-2">
        {isCompleted ? (
          <span className="px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
            {t('completed')}
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(task); }}
            className="px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-sm font-medium hover:bg-emerald-100 transition"
          >
            <Check size={14} className="inline mr-1" />
            {t('complete')}
          </button>
        )}
      </div>
    </div>
  );
});

// ---------- Modal ----------
function Modal({ open, title, children, onClose, onSave, t }) {
  const [saving, setSaving] = useState(false);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="w-full sm:max-w-xl bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="h-2 bg-[#007cf0] flex-shrink-0" />
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-xl">{title}</h2>
            <button className="p-2 hover:bg-slate-100 rounded-full" onClick={onClose} aria-label="Close">
              <X size={20} />
            </button>
          </div>
          {children}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-6">
            <button
              className="px-4 py-3 sm:py-2 rounded-xl border border-slate-200 font-medium"
              onClick={onClose}
              disabled={saving}
            >
              {t('cancel')}
            </button>
            <button
              className="px-6 py-3 sm:py-2 rounded-xl bg-[#007cf0] text-white font-medium disabled:opacity-60 hover:bg-[#0066cc] transition-colors"
              onClick={async () => { setSaving(true); await onSave(); setSaving(false); }}
              disabled={saving}
            >
              {saving ? <Loader2 size={18} className="animate-spin mx-auto" /> : t('createTask')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Section ----------
function Section({ title, count, tone, children, t, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const tones = {
    red: "bg-red-50 border-red-200",
    yellow: "bg-yellow-50 border-yellow-200",
    gray: "bg-gray-50 border-gray-200",
    green: "bg-green-50 border-green-200",
  };
  const icons = {
    red: <AlertTriangle size={16} className="text-red-500" />,
    yellow: <Clock size={16} className="text-yellow-600" />,
    gray: <Calendar size={16} className="text-gray-500" />,
    green: <CheckCircle2 size={16} className="text-green-500" />,
  };

  return (
    <div className="mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-2 text-left"
      >
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          {icons[tone]}
          {title}
          {count > 0 && <span className="text-slate-500 font-normal text-sm">({count})</span>}
        </div>
        <ChevronDown size={20} className={`text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && count > 0 && (
        <div className={`rounded-xl border overflow-hidden ${tones[tone] || "bg-white border-gray-200"}`}>
          {children}
        </div>
      )}
      {open && count === 0 && (
        <div className="text-center py-6 text-slate-400 text-sm">
          {t('noTasksMatch')}
        </div>
      )}
    </div>
  );
}

// ---------- Main Component ----------
export default function Tasks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState("all");
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const taskRefs = useRef({});

  const now = new Date();

  // Handle URL query parameters
  useEffect(() => {
    const taskId = searchParams.get('taskId');
    const filter = searchParams.get('filter');

    if (taskId) {
      setHighlightedTaskId(taskId);
      setSelectedTask(taskId);
      setSearchParams({}, { replace: true });
      setTimeout(() => setHighlightedTaskId(null), 3000);
    }

    // Handle filter parameter to show due/overdue tasks
    if (filter === 'due') {
      setTab('dueToday');
      setSearchParams({}, { replace: true });
    } else if (filter === 'overdue') {
      setTab('overdue');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Scroll to highlighted task when items load
  useEffect(() => {
    if (highlightedTaskId && items.length > 0 && taskRefs.current[highlightedTaskId]) {
      taskRefs.current[highlightedTaskId].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightedTaskId, items]);

  useEffect(() => {
    async function loadClients() {
      try {
        const res = await getClients();
        setClients(res.data || []);
      } catch (err) {
        console.error('Failed to load clients:', err);
      }
    }
    loadClients();
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: query || "" };
      const res = scope === "all" ? await listAll(params) : await listMine(params);
      setItems(res.data || []);
      try { const s = await summary(); setSummaryData(s.data); } catch {}
      setSelected([]);
    } finally {
      setLoading(false);
    }
  }, [scope, query]);

  useEffect(() => {
    refresh();
    const onAnyTaskChange = () => refresh();
    window.addEventListener("tasks:update", onAnyTaskChange);
    return () => window.removeEventListener("tasks:update", onAnyTaskChange);
  }, [refresh]);

  const filtered = useMemo(() => {
    if (!query) return items;
    const s = query.toLowerCase();
    return items.filter((t) =>
      (t.title || "").toLowerCase().includes(s) ||
      (t.description || "").toLowerCase().includes(s) ||
      (t.clientName || "").toLowerCase().includes(s)
    );
  }, [items, query]);

  const buckets = useMemo(() => {
    const overdue = [], dueToday = [], upcoming = [], completed = [];
    for (const t of filtered) {
      if (t.status === "done" || t.status === "completed") { completed.push(t); continue; }
      if (!t.dueDate) { upcoming.push(t); continue; }
      const d = new Date(t.dueDate);
      if (d < startOfDay(now)) overdue.push(t);
      else if (sameDay(d, now)) dueToday.push(t);
      else upcoming.push(t);
    }
    return { overdue, dueToday, upcoming, completed };
  }, [filtered, now]);

  function toggleSelect(task) {
    setSelected((cur) =>
      cur.find((x) => x.id === task.id) ? cur.filter((x) => x.id !== task.id) : [...cur, task]
    );
  }

  async function toggleDone(task) {
    const newStatus = task.status === "done" || task.status === "completed" ? "pending" : "completed";
    if (task.clientId) {
      await patchClient(task.clientId, task.id, { status: newStatus });
    } else {
      await patchMine(task.id, { status: newStatus });
    }
    window.dispatchEvent(new CustomEvent("tasks:update"));
  }

  async function bulkComplete() {
    for (const t of selected) {
      if (t.status !== "done" && t.status !== "completed") {
        if (t.clientId) await patchClient(t.clientId, t.id, { status: "completed" });
        else await patchMine(t.id, { status: "completed" });
      }
    }
    window.dispatchEvent(new CustomEvent("tasks:update"));
  }

  function handleTaskClick(task) {
    setSelectedTask(task.id);
  }

  function handleTaskUpdate(updatedTask) {
    setItems(prev => prev.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t));
    window.dispatchEvent(new CustomEvent("tasks:update"));
  }

  function handleTaskDelete(taskId) {
    setItems(prev => prev.filter(t => t.id !== taskId));
    setSelectedTask(null);
  }

  async function handleTaskAction(action, task) {
    if (!task) return;

    switch (action) {
      case 'complete': {
        const newStatus = task.status === 'done' || task.status === 'completed' ? 'pending' : 'completed';
        try {
          if (task.clientId) {
            await patchClient(task.clientId, task.id, { status: newStatus });
          } else {
            await patchMine(task.id, { status: newStatus });
          }
          window.dispatchEvent(new CustomEvent('tasks:update'));
        } catch (err) {
          console.error('Failed to complete task:', err);
          alert('Failed to update task status.');
        }
        break;
      }
      case 'snooze': {
        const hours = window.prompt('Snooze for how many hours?', '24');
        if (!hours || isNaN(Number(hours))) return;
        const newDue = new Date(Date.now() + Number(hours) * 60 * 60 * 1000);
        try {
          if (task.clientId) {
            await patchClient(task.clientId, task.id, { dueDate: newDue.toISOString() });
          } else {
            await patchMine(task.id, { dueDate: newDue.toISOString() });
          }
          window.dispatchEvent(new CustomEvent('tasks:update'));
        } catch (err) {
          console.error('Failed to snooze task:', err);
          alert('Failed to snooze task.');
        }
        break;
      }
      case 'call':
      case 'sms':
      case 'email': {
        if (task.clientId) {
          navigate(`/communications?clientId=${task.clientId}&mode=${action}`);
        } else {
          alert(`No client linked to this task. Link a client to use the ${action} action.`);
        }
        break;
      }
      default:
        console.log('Unknown task action:', action, task);
    }
  }

  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    priority: "normal",
    dueDate: "",
    clientId: "",
    taskType: "general"
  });

  async function saveNewTask() {
    const payload = {
      title: newTask.title.trim(),
      description: newTask.description.trim(),
      priority: newTask.priority,
      dueDate: newTask.dueDate ? new Date(newTask.dueDate).toISOString() : null,
      taskType: newTask.taskType
    };
    if (!payload.title) return;

    if (newTask.clientId) {
      await createClient(newTask.clientId, payload);
    } else {
      await createMine(payload);
    }

    setCreateOpen(false);
    setNewTask({ title: "", description: "", priority: "normal", dueDate: "", clientId: "", taskType: "general" });
    window.dispatchEvent(new CustomEvent("tasks:update"));
  }

  const tabs = [
    { id: "all", label: t('all'), count: filtered.length },
    { id: "dueToday", label: t('dueToday'), count: buckets.dueToday.length },
    { id: "upcoming", label: t('upcoming'), count: buckets.upcoming.length },
    { id: "overdue", label: t('overdue'), count: buckets.overdue.length },
    { id: "completed", label: t('completed'), count: buckets.completed.length },
  ];

  return (
    <div className="px-0 sm:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="px-4 sm:px-0 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">{t('tasks')}</h1>
            {summaryData && (
              <p className="text-sm text-slate-600">
                {(summaryData.all.pending || 0) + (summaryData.all.in_progress || 0)} {t('total')} • {buckets.overdue.length} {t('overdue')}
              </p>
            )}
          </div>

          {/* Desktop Actions */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              className={`px-4 py-2 rounded-xl border font-medium transition-colors ${scope === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}
              onClick={() => setScope("all")}
            >
              {t('allTasks')}
            </button>
            <button
              className={`px-4 py-2 rounded-xl border font-medium transition-colors ${scope === "mine" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 hover:border-slate-300"}`}
              onClick={() => setScope("mine")}
            >
              {t('myTasksOnly')}
            </button>
            <button
              className="px-4 py-2 rounded-xl bg-[#007cf0] text-white font-semibold flex items-center gap-2 hover:bg-[#0066cc] transition-colors"
              onClick={() => setCreateOpen(true)}
            >
              <Plus size={18} />
              {t('addTask')}
            </button>
          </div>

          {/* Mobile Actions */}
          <div className="sm:hidden flex items-center gap-2">
            <button
              className="flex-1 px-4 py-3 rounded-xl bg-[#007cf0] text-white font-semibold flex items-center justify-center gap-2"
              onClick={() => setCreateOpen(true)}
            >
              <Plus size={18} />
              {t('addTask')}
            </button>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="p-3 rounded-xl border border-slate-200"
            >
              <MoreHorizontal size={20} />
            </button>
          </div>
        </div>

        {/* Mobile Filters */}
        {showMobileFilters && (
          <div className="sm:hidden mt-3 flex gap-2">
            <button
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium ${scope === "all" ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}
              onClick={() => setScope("all")}
            >
              {t('allTasks')}
            </button>
            <button
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium ${scope === "mine" ? "bg-slate-900 text-white" : "bg-white border border-slate-200"}`}
              onClick={() => setScope("mine")}
            >
              {t('myTasksOnly')}
            </button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="px-4 sm:px-0 mb-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="w-full pl-10 pr-4 py-3 sm:py-2 border border-slate-200 rounded-xl sm:rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#007cf0]"
            placeholder={t('searchTasks')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Tabs - Horizontal scroll on mobile */}
      <div className="px-4 sm:px-0 mb-4">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
          {tabs.map((tabItem) => (
            <button
              key={tabItem.id}
              onClick={() => setTab(tabItem.id)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${
                tab === tabItem.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white border-slate-200 hover:border-slate-300"
              }`}
            >
              {tabItem.label}
              {tabItem.count > 0 && (
                <span className={`ml-1.5 ${tab === tabItem.id ? "text-slate-300" : "text-slate-400"}`}>
                  {tabItem.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk Actions */}
      {selected.length > 0 && (
        <div className="mx-4 sm:mx-0 mb-4 bg-blue-50 rounded-xl p-3 flex items-center justify-between">
          <span className="text-sm text-blue-700 font-medium">
            {selected.length} {t('selected')}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-blue-600 text-sm font-medium hover:bg-blue-50"
              onClick={bulkComplete}
            >
              {t('completeAll')}
            </button>
            <button
              className="px-3 py-1.5 rounded-lg bg-white border border-blue-200 text-blue-600 text-sm font-medium hover:bg-blue-50"
              onClick={() => setSelected([])}
            >
              {t('clearSelection')}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#007cf0] animate-spin" />
        </div>
      ) : tab === "all" ? (
        <div className="px-4 sm:px-0">
          {/* Mobile: Card sections */}
          <div className="sm:hidden space-y-4">
            {buckets.overdue.length > 0 && (
              <Section tone="red" title={t('overdue')} count={buckets.overdue.length} t={t}>
                <div className="p-2 space-y-2">
                  {buckets.overdue.map((task) => (
                    <TaskCard key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
                  ))}
                </div>
              </Section>
            )}
            <Section tone="yellow" title={t('dueToday')} count={buckets.dueToday.length} t={t}>
              <div className="p-2 space-y-2">
                {buckets.dueToday.map((task) => (
                  <TaskCard key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
                ))}
              </div>
            </Section>
            <Section tone="gray" title={t('upcoming')} count={buckets.upcoming.length} t={t}>
              <div className="p-2 space-y-2">
                {buckets.upcoming.map((task) => (
                  <TaskCard key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
                ))}
              </div>
            </Section>
            <Section tone="green" title={t('completed')} count={buckets.completed.length} t={t} defaultOpen={false}>
              <div className="p-2 space-y-2">
                {buckets.completed.map((task) => (
                  <TaskCard key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
                ))}
              </div>
            </Section>
          </div>

          {/* Desktop: Table sections */}
          <div className="hidden sm:block space-y-6">
            <Section tone="red" title={t('overdue')} count={buckets.overdue.length} t={t}>
              {buckets.overdue.map((task) => (
                <Row key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
              ))}
            </Section>
            <Section tone="yellow" title={t('dueToday')} count={buckets.dueToday.length} t={t}>
              {buckets.dueToday.map((task) => (
                <Row key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
              ))}
            </Section>
            <Section tone="gray" title={t('upcoming')} count={buckets.upcoming.length} t={t}>
              {buckets.upcoming.map((task) => (
                <Row key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
              ))}
            </Section>
            <Section tone="green" title={t('completed')} count={buckets.completed.length} t={t} defaultOpen={false}>
              {buckets.completed.map((task) => (
                <Row key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
              ))}
            </Section>
          </div>
        </div>
      ) : (
        <div className="px-4 sm:px-0">
          {(() => {
            const visible =
              tab === "overdue" ? buckets.overdue :
              tab === "dueToday" ? buckets.dueToday :
              tab === "upcoming" ? buckets.upcoming :
              tab === "completed" ? buckets.completed : filtered;

            if (visible.length === 0) {
              return (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 size={48} className="mx-auto mb-3 text-slate-300" />
                  <p className="text-lg font-medium">{t('noTasksMatch')}</p>
                </div>
              );
            }

            return (
              <>
                {/* Mobile Cards */}
                <div className="sm:hidden space-y-3">
                  {visible.map((task) => (
                    <TaskCard key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
                  ))}
                </div>
                {/* Desktop Rows */}
                <div className="hidden sm:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                  {visible.map((task) => (
                    <Row key={task.id} ref={el => taskRefs.current[task.id] = el} task={task} selected={!!selected.find(x=>x.id===task.id)} onSelect={toggleSelect} onToggle={toggleDone} onClick={handleTaskClick} t={t} highlighted={highlightedTaskId === task.id} />
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Create Task Modal */}
      <Modal open={createOpen} title={t('createNewTask')} onClose={() => setCreateOpen(false)} onSave={saveNewTask} t={t}>
        <div className="space-y-4">
          {/* Task Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">{t('taskType')}</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(TASK_TYPES).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewTask(v => ({ ...v, taskType: key }))}
                    className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-colors ${
                      newTask.taskType === key
                        ? 'border-[#007cf0] bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                      <Icon size={20} />
                    </div>
                    <span className="text-xs font-medium text-center">{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium mb-1.5">{t('linkToClient')}</label>
            <select
              className="w-full border border-slate-200 rounded-xl px-3 py-3 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-[#007cf0]"
              value={newTask.clientId}
              onChange={(e) => setNewTask(v => ({ ...v, clientId: e.target.value }))}
            >
              <option value="">{t('noClientPersonalTask')}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1.5">{t('title')}</label>
            <input
              className="w-full border border-slate-200 rounded-xl px-3 py-3 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-[#007cf0]"
              value={newTask.title}
              onChange={(e) => setNewTask(v => ({ ...v, title: e.target.value }))}
              placeholder={t('taskTitlePlaceholder')}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1.5">{t('description')}</label>
            <textarea
              className="w-full border border-slate-200 rounded-xl px-3 py-3 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-[#007cf0]"
              rows={3}
              value={newTask.description}
              onChange={(e) => setNewTask(v => ({ ...v, description: e.target.value }))}
              placeholder={t('taskDescriptionPlaceholder')}
            />
          </div>

          {/* Priority & Due Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('priority')}</label>
              <select
                className="w-full border border-slate-200 rounded-xl px-3 py-3 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-[#007cf0]"
                value={newTask.priority}
                onChange={(e) => setNewTask(v => ({ ...v, priority: e.target.value }))}
              >
                <option value="low">{t('low')}</option>
                <option value="normal">{t('normal')}</option>
                <option value="high">{t('high')}</option>
                <option value="urgent">{t('urgent')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('due')}</label>
              <input
                type="datetime-local"
                className="w-full border border-slate-200 rounded-xl px-3 py-3 sm:py-2 text-base sm:text-sm focus:ring-2 focus:ring-[#007cf0]"
                value={newTask.dueDate}
                onChange={(e) => setNewTask(v => ({ ...v, dueDate: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Task Detail Drawer */}
      {selectedTask && (
        <TaskDetailDrawer
          taskId={selectedTask}
          onClose={() => setSelectedTask(null)}
          onUpdate={handleTaskUpdate}
          onDelete={handleTaskDelete}
          onAction={handleTaskAction}
        />
      )}
    </div>
  );
}
